'use client';

import clsx from 'clsx';
import React from 'react';
import { Popover, PopoverButton, PopoverPanel, PopoverProps } from '@headlessui/react';
import DSButton from './Button';
import Dialog from './Dialog';
import Icon, { IconName } from './Icon';
import { SearchInput, SelectionRow } from './Input';

/**
 * DS `Filters / Sub Navigation` — TR - Design System [MASTER], page 2:43.
 *
 * Measured, not inferred. Chips are 33 tall — the same rung as Mini Button —
 * and move surface-100 → surface-300 when their dropdown is open. The active
 * state keeps the surface-100 fill but gains a count badge and a clear
 * control, so "has a value" and "is open" read as different things rather
 * than competing for the same affordance.
 *
 * Dropdown rows are `SelectionRow` and the panel search is `SearchInput`, so
 * this composes the primitives rather than restating them.
 */

const CHIP_BASE =
    'text-h4 inline-flex h-[33px] shrink-0 items-center gap-[8px] rounded-none px-[16px] text-ink-400 transition-colors duration-dropdown ease '
    // The system draws no focus state (Q16), so this follows DS Button: the
    // same surface-600 outline, and only for keyboard focus. Without it these
    // fell back to the browser's own blue ring.
    + 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-surface-600';

/**
 * The chip's classes, exported so surfaces that must own their own trigger
 * element can still look like a chip. SearchFilters needs this: its trigger is
 * a headlessui PopoverButton, so it cannot nest FilterChip inside without
 * losing the popover wiring.
 */
export const filterChipClass = ({
    open = false,
    disabled = false
}: { open?: boolean; disabled?: boolean } = {}) =>
    clsx(
        CHIP_BASE,
        open ? 'bg-surface-300' : 'bg-surface-100 hover:enabled:bg-surface-200',
        disabled && 'cursor-not-allowed text-surface-400'
    );

/**
 * Popovers sit on a solid fill. No border: design draws these without a
 * stroke, and with the panel and the page both on surface-100 a border was
 * the only thing separating them — so the shadow modals use takes that job.
 *
 * The panel does NOT scroll. Whatever it wraps owns its own scroller —
 * FilterDropdown caps its option list at 320px, and SearchFilters caps its
 * list at 30vh — so a max-height and overflow here as well gave one list two
 * nested scrollbars: the outer sliding the whole panel, search field
 * included, while the inner moved the rows. Only a list long enough to fill
 * the inner cap showed it, which made it look specific to Sub Categories.
 *
 * A wrapper that scrolls its own scrolling child is always wrong; the cap
 * belongs with the list that has the rows.
 */
export const filterPanelClass =
    'z-10 rounded-none bg-surface-100 p-[8px] shadow-ds-big duration-dropdown ease-ds-out';

export type FilterChipProps = {
    label: string;
    /** Number of applied values. Renders the count badge and clear control. */
    count?: number;
    open?: boolean;
    onToggle?: () => void;
    onClear?: () => void;
    className?: string;
};

export const FilterChip = ({
    label,
    count,
    open = false,
    onToggle,
    onClear,
    className
}: FilterChipProps) => {
    const active = Boolean(count);

    return (
        <span
            className={clsx(
                CHIP_BASE,
                open ? 'bg-surface-300' : 'bg-surface-100 hover:bg-surface-200',
                className
            )}
        >
            <button type="button" onClick={onToggle} className="inline-flex items-center gap-[8px]">
                {label}
                {active && (
                    <span className="text-body-600 inline-flex size-[18px] items-center justify-center bg-surface-300 text-ink-400">
                        {count}
                    </span>
                )}
                {!active && <Icon name="chevron-down" size={16} className={clsx(open && 'rotate-180')} />}
            </button>

            {/* Clearing is a separate target from opening — the DS draws them as
                two controls in one chip, not one control with two meanings. */}
            {active && (
                <button type="button" onClick={onClear} aria-label={`Clear ${label}`}>
                    <Icon name="close" size={14} />
                </button>
            )}
        </span>
    );
};

export type FilterBarProps = {
    children: React.ReactNode;
    /**
     * Renders the leading clear-all control. Deliberately separate from
     * `onClearAll`: deriving visibility from handler presence forces every
     * caller to pass a function purely to make something appear, which is not
     * possible from a server component.
     */
    showClearAll?: boolean;
    onClearAll?: () => void;
    className?: string;
};

export const FilterBar = ({ children, showClearAll = false, onClearAll, className }: FilterBarProps) => (
    <div className={clsx('flex flex-wrap items-center gap-[8px]', className)}>
        {showClearAll && (
            <button
                type="button"
                onClick={onClearAll}
                aria-label="Clear all filters"
                className="inline-flex size-[33px] shrink-0 items-center justify-center text-ink-400 transition-colors hover:bg-surface-200"
            >
                <Icon name="close" size={16} />
            </button>
        )}
        {children}
    </div>
);

/** Below this much left to scroll, the fade has nothing left to promise. */
const FADE_THRESHOLD = 20;

/**
 * True while a scroller has content below the fold — and, once scrolling has
 * started, only while more than 20px of it is left.
 *
 * The comp's bottom fade only makes sense when there is something to scroll
 * to. It used to answer "is this list scrollable at all", so it stayed lit at
 * the foot of the list and greyed out the last row you had just scrolled to.
 * Measured rather than guessed from a row count, so it stays right if the row
 * height changes.
 */
export const useOverflows = (ref: React.RefObject<HTMLElement>, deps: unknown[]) => {
    const [overflows, setOverflows] = React.useState(false);

    React.useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;

        const measure = () => setOverflows(
            el.scrollHeight - el.clientHeight - el.scrollTop > FADE_THRESHOLD
        );
        measure();

        el.addEventListener('scroll', measure, { passive: true });

        // The panel can be measured before fonts settle, and the list is
        // filtered live, so watch the box rather than measuring once.
        const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure);
        observer?.observe(el);
        return () => {
            el.removeEventListener('scroll', measure);
            observer?.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    return overflows;
};

/**
 * Sort / show menu — the chip trigger plus its panel, in one place.
 *
 * `SortControl` above is only the trigger; every surface that needed the menu
 * built its own, and four of them ended up with the same legacy dropdown:
 * a text-sm blue trigger over a rounded-md white panel on shadow-2xl and a
 * black ring. That is the shape this replaces, and it exists so there is no
 * "old one" left to copy.
 *
 * Options come in already translated — the DS does not reach for i18n — and
 * are addressed by id so a caller can map back to whatever its own option
 * type is.
 *
 * The trigger carries the label alone by default, with the active option
 * marked in the panel, which is what the page comps draw. `showActiveValue`
 * spells it out on the trigger instead, for a control that filters what you
 * are looking at rather than ordering it, where the current value is not
 * obvious from the list below.
 */
/**
 * Opens a dropdown on hover, on pointer devices only.
 *
 * Desktop wants these on hover; the click path stays for touch, where hover
 * does not exist and the chips open a full filter panel instead. So the
 * handlers no-op unless the device actually has a pointer — `(hover: hover)`
 * is the same test the CSS uses.
 *
 * It drives whatever trigger the wrapper contains rather than owning state,
 * because these are headlessui Popovers and Menus whose open state is their
 * own: a click on the button is the one way in that works for both. The
 * button reports its state through aria-expanded, which is how this knows
 * whether a click would open or close.
 *
 * Leaving is delayed. The panel hangs 8px below its button, and crossing that
 * gap takes the pointer briefly outside the wrapper; closing immediately
 * would make the menu unreachable.
 */
export const useHoverOpen = (delay = 140) => {
    const ref = React.useRef<HTMLDivElement>(null);
    const timer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    /** When the pointer last opened the panel, so the click behind it can be ignored. */
    const openedByHover = React.useRef(0);

    React.useEffect(() => () => clearTimeout(timer.current), []);

    const canHover = () => typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches;
    const trigger = () => ref.current?.querySelector<HTMLElement>('button[aria-expanded]');
    const isOpen = () => trigger()?.getAttribute('aria-expanded') === 'true';

    return {
        ref,
        /*
         * Hover opens the panel, and the click that follows used to shut it
         * again: arriving at a chip fires mouseenter first, so by the time
         * the button sees the click it is already open and a click toggles.
         * Measured on the live site — open, then closed 6ms later, on one
         * gesture. The pointer's own click is swallowed inside that window.
         */
        onClickCapture: (e: React.MouseEvent) => {
            if (Date.now() - openedByHover.current > 400) return;
            const button = trigger();
            if (!button || !button.contains(e.target as Node)) return;
            openedByHover.current = 0;
            e.preventDefault();
            e.stopPropagation();
        },
        onMouseEnter: () => {
            clearTimeout(timer.current);
            if (!canHover() || isOpen()) return;
            const button = trigger();
            button?.click();
            /*
             * Armed AFTER the click, never before: this click is synchronous
             * and bubbles straight back into onClickCapture, so arming first
             * made the guard swallow the one click that opens the panel.
             */
            openedByHover.current = Date.now();
            /*
             * A programmatic click is not a pointer gesture, so the browser
             * treats the focus that follows as keyboard focus and draws its
             * focus ring — a blue one round every chip the pointer crossed.
             * Dropping focus leaves the panel open and keeps the ring for
             * people who actually tabbed to it.
             */
            button?.blur();
        },
        /*
         * `relatedTarget` is where the pointer went. Moving from the chip
         * into its own panel is not leaving, and neither is the chip
         * shrinking out from under the pointer when a selection is cleared —
         * which is what made a filter close and reopen on its own after the
         * last chip was unticked.
         */
        onMouseLeave: (e: React.MouseEvent) => {
            if (!canHover()) return;
            const next = e.relatedTarget as Node | null;
            if (next && ref.current?.contains(next)) return;
            clearTimeout(timer.current);
            timer.current = setTimeout(() => {
                const button = trigger();
                if (!button || button.getAttribute('aria-expanded') !== 'true') return;
                button.click();
                /*
                 * Menu hands focus back to its button when it closes, unlike
                 * Popover — and that restored focus counts as keyboard focus,
                 * so the ring came back on the way out. Drop it again.
                 */
                button.blur();
            }, delay);
        }
    };
};

/**
 * Closes an open panel once the page scrolls under it.
 *
 * These panels are anchored, which means fixed positioning and a floating-ui
 * loop re-measuring the trigger on every frame. Scrolling with one open makes
 * the panel chase its chip a frame behind — and as the chip nears the edge of
 * the viewport the shift/flip fallback slides the panel back into view, which
 * is the part that reads as a jump.
 *
 * Following the page perfectly would mean positioning the panel absolutely
 * against its chip instead, which is cheaper and smoother but gives up the
 * one thing the anchor is doing for us: a 292px panel hanging off a chip in
 * the right half of a 375px screen would run off the edge with nothing to
 * push it back. So the panel keeps its anchor and stops existing during the
 * scroll instead — which is what a phone expects anyway.
 *
 * The first scroll event while open only records where we started: opening
 * can itself nudge the page, and closing on that would make the chip look
 * broken. Anything past 8px after that is a real scroll.
 */
const useCloseOnScroll = (ref: React.RefObject<HTMLDivElement | null>) => {
    React.useEffect(() => {
        let origin: number | null = null;

        const onScroll = () => {
            const button = ref.current?.querySelector<HTMLElement>('button[aria-expanded="true"]');
            if (!button) {
                origin = null;
                return;
            }
            if (origin === null) {
                origin = window.scrollY;
                return;
            }
            if (Math.abs(window.scrollY - origin) < 8) return;
            origin = null;
            button.click();
            // Same reason as useHoverOpen: a programmatic click leaves the
            // keyboard focus ring behind on a chip nobody tabbed to.
            button.blur();
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [ref]);
};

export const HoverPopover = ({ children, ...rest }: PopoverProps) => {
    const hover = useHoverOpen();
    useCloseOnScroll(hover.ref);

    return (
        <Popover
            {...rest}
            ref={hover.ref}
            onClickCapture={hover.onClickCapture}
            onMouseEnter={hover.onMouseEnter}
            onMouseLeave={hover.onMouseLeave}
        >
            {children}
        </Popover>
    );
};

export type SortDirection = 'asc' | 'desc';

export type SortMenuOption = {
    id: string;
    label: string;
    icon?: IconName;
    /** A row that can be read both ways. Picking it again flips it. */
    reversible?: boolean;
};

/**
 * Sort — the filter chips' own Popover, not a Menu.
 *
 * Menu locks the document's scroll while it is open (headlessui sets
 * overflow: hidden on the html element), which reclaims the scrollbar's width
 * and slides every centred container sideways for as long as the menu is up.
 * Popover does not, and the filter chips beside this were already using it
 * without that problem — so this is the same component rather than a fight
 * with the other one.
 *
 * Popover does not close itself when something inside is clicked, so each
 * option closes the panel through the render prop's `close`.
 *
 * A row that reads both ways marks its direction instead of ticking itself:
 * a checkmark can only say "this one", which left ascending and descending
 * as two rows saying almost the same thing, while an arrow says "this one,
 * this way" and collapses the pair into a row you press again to turn
 * around. Rows with one order keep the checkmark — an arrow there would
 * offer a direction that does not exist.
 *
 * The trigger stays the resting label while the default order is in force,
 * since "Sort" is the question and nothing has answered it yet. Once the
 * reader picks something else it carries that instead — mark, name, and
 * direction where there is one — with an x to put it back, the same two
 * targets FilterChip uses.
 */
export const SortMenu = ({
    label,
    options,
    activeId,
    defaultId,
    activeDirection = 'asc',
    onChange,
    onClear,
    icon = 'sort',
    prefixLabel = false,
    className
}: {
    label: string;
    options: SortMenuOption[];
    activeId?: string;
    /** The resting order. While it is the active one the trigger stays `label`. */
    defaultId?: string;
    /** `null` for a plain selector — no direction, so the row ticks instead. */
    activeDirection?: SortDirection | null;
    onChange: (id: string, direction: SortDirection) => void;
    onClear?: () => void;
    icon?: IconName;
    /** Keeps the resting label in front of the choice, as "Show: Received". */
    prefixLabel?: boolean;
    className?: string;
}) => {
    const active = options.find((option) => option.id === activeId);
    const directional = activeDirection !== null;
    const arrow: IconName = activeDirection === 'desc' ? 'arrow-down' : 'arrow-up';
    // Only a row that can be turned around has a direction worth drawing.
    const markFor = (option: SortMenuOption): IconName => (directional && option.reversible ? arrow : 'check');
    const showSelection = Boolean(active) && active?.id !== defaultId;

    return (
        <HoverPopover className={clsx('relative text-left', className)}>
            <span className={clsx(filterChipClass(), 'flex w-full justify-between')}>
                {/* The button takes the chip's width so its mark sits on the
                    far edge rather than trailing the word — the chip is a
                    field, and the mark is its affordance, not a suffix. */}
                <PopoverButton className="flex min-w-0 flex-1 items-center justify-between gap-[8px] focus:outline-none">
                    {active && showSelection ? (
                        <>
                            <span className="inline-flex min-w-0 items-center gap-[8px]">
                                <span className="flex size-[16px] shrink-0 items-center justify-center">
                                    {active.icon && <Icon name={active.icon} size={16} />}
                                </span>
                                <span className="truncate">{prefixLabel ? `${label}: ${active.label}` : active.label}</span>
                            </span>
                            {directional && active.reversible && <Icon name={arrow} size={16} className="shrink-0" />}
                        </>
                    ) : (
                        <>
                            {label}
                            <Icon name={icon} size={16} className="shrink-0" />
                        </>
                    )}
                </PopoverButton>

                {/* Clearing is its own target, as it is on the filter chips —
                    one control with two meanings is what the X avoids. */}
                {showSelection && onClear && (
                    <button type="button" onClick={onClear} aria-label={`Clear ${label}`}>
                        <Icon name="close" size={14} />
                    </button>
                )}
            </span>
            <PopoverPanel
                transition
                data-no-focus-ring
                anchor={{ to: 'bottom start', gap: 8, padding: 16 }}
                className={clsx(
                    'z-20 w-[220px] origin-top-left transition focus:outline-none',
                    'data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0',
                    'data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in',
                    // overscroll-contain so running out of list inside the
                    // panel does not hand the rest of the gesture to the page,
                    // which would then close the panel under the reader.
                    'max-h-[50vh] overflow-y-auto overscroll-contain',
                    filterPanelClass
                )}
            >
                {({ close }) => (
                    <>
                        {options.map((option) => {
                            const isActive = option.id === activeId;
                            const next: SortDirection = isActive && option.reversible && activeDirection === 'asc'
                                ? 'desc'
                                : 'asc';

                            return (
                                <button
                                    key={option.id}
                                    type="button"
                                    onClick={() => {
                                        onChange(option.id, next);
                                        // A reversible row is a control the
                                        // reader may want twice; closing under
                                        // them costs a second open to see the
                                        // other direction.
                                        if (!(isActive && option.reversible)) close();
                                    }}
                                    className={clsx(
                                        'text-body-400 flex w-full items-center justify-between gap-[8px] px-[12px] py-[8px] text-left transition-colors hover:bg-surface-200',
                                        isActive ? 'bg-surface-200 text-ink-400' : 'text-ink-300'
                                    )}
                                >
                                    <span className="flex min-w-0 items-center gap-[8px]">
                                        {/* The mark sits with the label; options
                                            without one keep the same text origin. */}
                                        <span className="flex size-[16px] shrink-0 items-center justify-center">
                                            {option.icon && <Icon name={option.icon} size={16} />}
                                        </span>
                                        <span className="truncate">{option.label}</span>
                                    </span>
                                    {isActive && <Icon name={markFor(option)} size={16} className="shrink-0" />}
                                </button>
                            );
                        })}
                    </>
                )}
            </PopoverPanel>
        </HoverPopover>
    );
};

export type FilterOption = { value: string; label: string; iconUrl?: string };

export type FilterDropdownProps = {
    options: FilterOption[];
    selected?: string[];
    onChange?: (value: string, next: boolean) => void;
    /** Adds the in-panel search field once the list is long enough to need it. */
    searchable?: boolean;
    control?: 'checkbox' | 'radio';
    className?: string;
};

export const FilterDropdown = ({
    options,
    selected = [],
    onChange,
    searchable = false,
    control = 'checkbox',
    className
}: FilterDropdownProps) => {
    const listRef = React.useRef<HTMLDivElement>(null);
    const overflows = useOverflows(listRef, [options.length]);

    return (
        <div className={clsx('flex w-[292px] flex-col gap-[8px] bg-surface-100 p-[8px]', className)}>
            {searchable && <SearchInput fluid />}
            {/* 312 and the DS scroll rail, per master 396:7113. Was 320, measured
            before that node existed. The comp fades the last rows out into the
            panel fill rather than cutting them off (396:7162). */}
            <div className="relative">
                <div ref={listRef} className="ds-scrollbar flex max-h-[312px] flex-col overflow-y-auto overscroll-contain">
                    {options.map((option) => (
                        <SelectionRow
                            key={option.value}
                            label={option.label}
                            iconUrl={option.iconUrl}
                            control={control}
                            selected={selected.includes(option.value)}
                            onChange={(next) => onChange?.(option.value, next)}
                        />
                    ))}
                </div>
                <div
                    aria-hidden
                    className={clsx(
                        'pointer-events-none absolute inset-x-0 bottom-0 h-[71px] bg-gradient-to-b from-transparent to-surface-100',
                        'duration-dropdown ease-ds-out transition-opacity',
                        overflows ? 'opacity-100' : 'opacity-0'
                    )}
                />
            </div>
        </div>
    );
};

export type SortControlProps = {
    label?: string;
    icon?: IconName;
    onToggle?: () => void;
    open?: boolean;
    className?: string;
};

/** Sort is a chip like the others, but carries the sort mark rather than a chevron. */
export const SortControl = ({
    label = 'Sort',
    icon = 'sort',
    onToggle,
    open = false,
    className
}: SortControlProps) => (
    <button
        type="button"
        onClick={onToggle}
        className={clsx(
            CHIP_BASE,
            open ? 'bg-surface-300' : 'bg-surface-100 hover:bg-surface-200',
            className
        )}
    >
        {label}
        <Icon name={icon} size={16} />
    </button>
);

/**
 * Mobile filter modal — TR - Design System [MASTER], Filters page (2:43).
 *
 * Two states on the sheet: a root list of filter groups, and a drilled-in
 * panel for one group. Same drilldown shape as the mobile nav drawer, so it
 * takes `group` to decide which it is rather than being two components.
 *
 * Apply is always present and always primary; the sheet keeps it pinned even
 * on the root list, so a user can dismiss with their selections intact rather
 * than having to back out first.
 */
export const FilterModal = ({
    open,
    groups,
    group,
    options = [],
    selected = [],
    onOpenGroup,
    onBack,
    onToggle,
    onApply,
    onClose,
    className
}: {
    open: boolean;
    /** Root list of filter groups. */
    groups: { id: string; label: string }[];
    /** When set, the modal shows this group's options instead of the root list. */
    group?: { id: string; label: string };
    options?: FilterOption[];
    selected?: string[];
    onOpenGroup?: (id: string) => void;
    onBack?: () => void;
    onToggle?: (value: string, next: boolean) => void;
    onApply?: () => void;
    onClose?: () => void;
    className?: string;
}) => (
    <Dialog
        open={open}
        title={group ? group.label : 'Filters'}
        onClose={onClose}
        width={382}
        className={className}
        actions={(
            <>
                {group && (
                    <DSButton variant="secondary" iconOnly aria-label="Back" onClick={onBack} icon={<Icon name="chevron-right" size={16} className="rotate-180" />} />
                )}
                <DSButton variant="primary" fullWidth onClick={onApply} icon={<Icon name="arrow-right" />}>
                    Apply
                </DSButton>
            </>
        )}
    >
        {group ? (
            <div className="flex flex-col gap-[8px]">
                <SearchInput fluid />
                <div>
                    {options.map((option) => (
                        <SelectionRow
                            key={option.value}
                            label={option.label}
                            iconUrl={option.iconUrl}
                            selected={selected.includes(option.value)}
                            onChange={(next) => onToggle?.(option.value, next)}
                        />
                    ))}
                </div>
            </div>
        ) : (
            <div className="flex flex-col">
                {groups.map((entry) => (
                    <button
                        key={entry.id}
                        type="button"
                        onClick={() => onOpenGroup?.(entry.id)}
                        className="text-body-400 flex h-[48px] w-full items-center gap-[12px] px-[4px] text-ink-400 transition-colors hover:bg-surface-100"
                    >
                        <span className="min-w-0 flex-1 truncate text-left">{entry.label}</span>
                        <Icon name="chevron-right" size={16} className="shrink-0 text-ink-300" />
                    </button>
                ))}
            </div>
        )}
    </Dialog>
);
